import { Entity } from "@common/types/entity";
import { BulkUpdateParams } from "../../../transactions/TransactionsBulkActions/lib";
import {
    extractRowActionValue,
    isEntityValue,
    RowActionValue,
    stringifyActionValue,
} from "./seedActionRow";
import { ActionRowDefinition } from "./types";

interface UpdatedValueSummary {
    updatedValueCount: number;
    singleValueToAssign?: BulkUpdateParams;
}

function hasUpdatedTransactions(
    row: ActionRowDefinition,
    rowIdToOwnedTxIds: Map<string, number[]>,
): boolean {
    return (
        row.isBaseAction ||
        !!row.filterScope ||
        (rowIdToOwnedTxIds.get(row.id)?.length ?? 0) > 0
    );
}

function resolveEntityValue(
    value: BulkUpdateParams["value"] | RowActionValue,
    entities: Entity[],
): Entity | undefined {
    if (isEntityValue(value)) {
        return value;
    }
    if (typeof value !== "number") {
        return undefined;
    }
    return entities.find((entity) => entity.id === value);
}

function buildValueToAssignForRowValue({
    valueToAssign,
    value,
    entities,
}: {
    valueToAssign: BulkUpdateParams;
    value: RowActionValue;
    entities: Entity[];
}): BulkUpdateParams | undefined {
    const isInitialValue =
        stringifyActionValue(valueToAssign.update, value) ===
        stringifyActionValue(valueToAssign.update, valueToAssign.value);

    switch (valueToAssign.update) {
        case "categoryId":
            return typeof value === "string"
                ? { update: "categoryId", value }
                : undefined;
        case "counterpartyId":
            return typeof value === "string" || value === null
                ? {
                      update: "counterpartyId",
                      value,
                      counterpartyName: isInitialValue
                          ? valueToAssign.counterpartyName
                          : undefined,
                  }
                : undefined;
        case "coa":
            return typeof value === "string" || value === null
                ? {
                      update: "coa",
                      value,
                      accountName: isInitialValue
                          ? valueToAssign.accountName
                          : undefined,
                  }
                : undefined;
        case "accrualCoa":
            return typeof value === "string" || value === null
                ? {
                      update: "accrualCoa",
                      value,
                      accountName: isInitialValue
                          ? valueToAssign.accountName
                          : undefined,
                  }
                : undefined;
        case "classes":
            return Array.isArray(value)
                ? { update: "classes", value }
                : undefined;
        case "entity": {
            const entity = resolveEntityValue(value, entities);
            return entity ? { update: "entity", value: entity } : undefined;
        }
        case "memo":
            return typeof value === "string"
                ? { update: "memo", value }
                : undefined;
        default:
            return undefined;
    }
}

export function getUpdatedValueSummary({
    rows,
    rowIdToOwnedTxIds,
    valueToAssign,
    entities,
}: {
    rows: ActionRowDefinition[];
    rowIdToOwnedTxIds: Map<string, number[]>;
    valueToAssign: BulkUpdateParams;
    entities: Entity[];
}): UpdatedValueSummary {
    const updatedValueByKey = new Map<string, BulkUpdateParams>();

    for (const row of rows) {
        if (!hasUpdatedTransactions(row, rowIdToOwnedTxIds)) {
            continue;
        }

        const value = extractRowActionValue(valueToAssign.update, row);
        if (value === undefined) {
            continue;
        }

        const key = stringifyActionValue(valueToAssign.update, value);
        if (updatedValueByKey.has(key)) {
            continue;
        }

        const rowValueToAssign = buildValueToAssignForRowValue({
            valueToAssign,
            value,
            entities,
        });
        if (rowValueToAssign) {
            updatedValueByKey.set(key, rowValueToAssign);
        }
    }

    const updatedValues = Array.from(updatedValueByKey.values());

    return {
        updatedValueCount: updatedValues.length,
        singleValueToAssign:
            updatedValues.length === 1 ? updatedValues[0] : undefined,
    };
}
